// Voice calls: every call, filterable, with a details drawer (timeline, plan, recording).
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import moment from 'moment';
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  Eye,
  Headphones,
  Mic,
  PhoneOff,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import {
  isLiveState,
  recordingUrl,
  useVoiceBoxesQuery,
  useVoiceCallQuery,
  useVoiceCallsQuery,
  useVoiceCancelCallMutation,
  useVoiceRefresh,
  useVoiceSuggestQuery,
} from '@/client/api/voice/voice.query';
import { CallsFilter, PlanItem, VoiceCall, VoiceEvent, VoiceParty, VoiceSuggestion } from '@/client/types/voice';
import useDebounce from '@/client/hooks/useDebounce';
import { Card, EmptyRow, Field, FormDialog, INPUT_CLASS, PageShell, Th } from '../Cloud/cloudUi';
import {
  AccessDenied,
  AudioPlayer,
  CallStatePill,
  ErrorNotice,
  FetchingChip,
  Kebab,
  LoadingNote,
  VOICE_BASE,
  VoicePerms,
  fmtShort,
  fmtTalk,
  fmtWhen,
  titleCase,
  useVoicePerms,
} from './voiceUi';

/* ---------- who and what ---------- */

const PartyCell: React.FC<{ party?: VoiceParty | null; fallback?: string | null }> = ({ party, fallback }) => {
  if (!party) {
    return fallback ? <span className="font-mono text-xs text-gray-500">{fallback}</span> : <span className="text-gray-300">—</span>;
  }
  const to = party.kind === 'user' ? `/admin/users/${party.id}` : `/admin/organizations/${party.id}`;
  return (
    <Link to={to} onClick={(e) => e.stopPropagation()} className="block min-w-0 hover:underline">
      <span className="block truncate text-gray-900">{party.name || `${party.kind} ${party.id}`}</span>
      {party.username && <span className="block truncate text-xs text-gray-500">@{party.username}</span>}
      {party.kind === 'org' && <span className="block text-xs text-gray-500">Organization</span>}
    </Link>
  );
};

const SLOT_NAME: Record<string, string> = {
  buyer_welcome: 'Welcome',
  recording_notice: 'Recording notice',
  seller_notice: 'Seller notice',
  hold: 'Hold',
  unavailable: 'Unavailable',
  system_failure: 'System failure',
  ad: 'Advert',
};
export const slotName = (slot: string) => SLOT_NAME[slot] ?? titleCase(slot);

/** One timeline line in plain words. */
export const describeEvent = (e: VoiceEvent): string => {
  const type = e.type ?? e.event_type ?? 'event';
  const p = e.payload ?? {};
  const str = (k: string) => (typeof p[k] === 'string' ? (p[k] as string) : '');
  switch (type) {
    case 'session.created':
      return 'Call requested by the buyer';
    case 'session.prompt_played': {
      const who = str('leg') === 'seller' ? 'the seller' : 'the buyer';
      const source = str('source') === 'builtin' ? 'built-in sound' : 'uploaded audio';
      return `Played "${slotName(str('slot'))}" to ${who} (${source})`;
    }
    case 'session.caller_rejected':
      return 'A call from a number that is not the buyer was turned away';
    case 'session.inbound_answered':
      return 'Buyer called in and was answered';
    case 'session.dialing':
    case 'session.dialling':
      return 'Dialling the seller';
    case 'session.bridged':
      return 'Buyer and seller connected';
    case 'session.completed':
      return 'Call ended';
    case 'session.cancelled':
      return 'Call cancelled';
    case 'session.expired':
      return 'Hold time ran out before the buyer called';
    case 'session.failed':
      return str('reason') ? `Call failed: ${titleCase(str('reason'))}` : 'Call failed';
    default:
      return titleCase(type.replace(/^session\./, ''));
  }
};

/* ---------- cancel ---------- */

const CancelDialog: React.FC<{ call: VoiceCall; onClose: () => void }> = ({ call, onClose }) => {
  const m = useVoiceCancelCallMutation();
  return (
    <FormDialog
      title="Cancel this call"
      description="The buyer's number stops working and, if the seller is on the line, the call is ended. This cannot be undone."
      confirmLabel="Cancel call"
      destructive
      busy={m.isPending}
      error={m.error}
      onClose={onClose}
      onConfirm={(reason) =>
        m.mutate(
          { sessionId: call.session_id, reason },
          {
            onSuccess: () => {
              toast.success('Call cancelled');
              onClose();
            },
          },
        )
      }
    >
      <p className="font-mono text-xs text-gray-500">{call.session_id}</p>
    </FormDialog>
  );
};

/* ---------- details drawer ---------- */

const PlanList: React.FC<{ title: string; items?: PlanItem[] }> = ({ title, items }) =>
  items && items.length ? (
    <div>
      <p className="text-xs font-medium text-gray-500">{title}</p>
      <ol className="mt-1 space-y-1 text-sm text-gray-800">
        {items.map((i, n) => (
          <li key={`${i.slot}-${n}`}>
            {n + 1}. {slotName(i.slot)}
            {!i.prompt_id && <span className="text-gray-400"> (built-in sound)</span>}
          </li>
        ))}
      </ol>
    </div>
  ) : null;

export const CallSheet: React.FC<{
  sessionId: string | null;
  perms: VoicePerms;
  onClose: () => void;
}> = ({ sessionId, perms, onClose }) => {
  const q = useVoiceCallQuery(sessionId);
  const call = q.data?.call;
  const events = q.data?.events ?? [];
  const [cancelling, setCancelling] = useState(false);
  return (
    <Sheet open={!!sessionId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="flex h-full w-[420px] flex-col overflow-y-auto border-l bg-background p-0 shadow-2xl sm:w-[600px]">
        <div className="border-b p-6">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-3">
              Call details {call && <CallStatePill state={call.state} />}
            </SheetTitle>
            <SheetDescription className="font-mono">{sessionId}</SheetDescription>
          </SheetHeader>
        </div>
        <div className="space-y-6 p-6">
          {q.isError && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
          {q.isLoading || !call ? (
            !q.isError && <LoadingNote>Loading this call and its events.</LoadingNote>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Buyer"><PartyCell party={call.buyer} fallback={call.buyer_ref} /></Field>
                <Field label="Seller"><PartyCell party={call.seller} fallback={call.seller_ref} /></Field>
                <Field label="Listing">
                  {call.listing ? (
                    <Link to={`/admin/listings/${call.listing.eid}`} className="hover:underline">
                      {call.listing.title || `Listing ${call.listing.eid}`}
                    </Link>
                  ) : (
                    call.external_reference ?? '—'
                  )}
                </Field>
                <Field label="Numbers"><NumbersList call={call} showParties={perms.canRecordings} /></Field>
                {call.store && <Field label="Store">{call.store.name || `Store ${call.store.storeid}`}</Field>}
                <Field label="Box">
                  {call.box_id ? (
                    <Link to={`${VOICE_BASE}/boxes/${encodeURIComponent(call.box_id)}`} className="font-mono text-xs hover:underline">
                      {call.box_id}
                    </Link>
                  ) : '—'}
                </Field>
                <Field label="Requested">{fmtWhen(call.created_at)}</Field>
                <Field label="Connected">{fmtWhen(call.bridged_at)}</Field>
                <Field label="Ended">{fmtWhen(call.ended_at)}</Field>
                <Field label="Talk time">{fmtTalk(call.talk_seconds)}</Field>
                {call.reason && <Field label="Reason">{titleCase(call.reason)}</Field>}
                {!!call.caller_rejected_count && <Field label="Turned away">{call.caller_rejected_count} call(s)</Field>}
              </div>

              {perms.canRecordings && call.recording_id && (
                <div>
                  <p className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-500">
                    <Headphones className="h-3.5 w-3.5" /> Recording
                  </p>
                  <AudioPlayer src={recordingUrl(call.session_id)} />
                </div>
              )}

              <div>
                <p className="mb-2 text-sm font-semibold text-gray-800">Timeline</p>
                {events.length === 0 ? (
                  <p className="text-sm text-gray-400">No events recorded for this call.</p>
                ) : (
                  <ol className="relative ml-2 space-y-4 border-l border-gray-200 pl-5">
                    {events.map((e, n) => (
                      <li key={e.sequence ?? n} className="relative">
                        <span className="absolute -left-[26px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-blue-500" />
                        <p className="text-sm text-gray-900">{describeEvent(e)}</p>
                        <p className="text-xs text-gray-500">{fmtWhen(e.occurred_at)}</p>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              {call.announcement_plan && (
                <div>
                  <p className="mb-2 text-sm font-semibold text-gray-800">Announcement plan</p>
                  <div className="grid grid-cols-2 gap-4">
                    <PlanList title="Played to the buyer" items={call.announcement_plan.buyer} />
                    <PlanList title="Played to the seller" items={call.announcement_plan.seller} />
                  </div>
                </div>
              )}

              {perms.canManage && isLiveState(call.state) && (
                <Button variant="destructive" size="sm" onClick={() => setCancelling(true)}>
                  <PhoneOff className="mr-2 h-3.5 w-3.5" /> Cancel call
                </Button>
              )}
            </>
          )}
        </div>
        {cancelling && call && <CancelDialog call={call} onClose={() => setCancelling(false)} />}
      </SheetContent>
    </Sheet>
  );
};

/** The call's four numbers in call order: buyer -> line they dialled -> line we
 *  rang from -> seller. The two party phones only reach voice.recordings admins. */
const NumbersList: React.FC<{ call: VoiceCall; showParties: boolean }> = ({ call, showParties }) => {
  const rows: [string, string | null | undefined][] = [
    ...(showParties ? [['Buyer', call.buyer_phones?.join(', ')] as [string, string | undefined]] : []),
    ['Dialled', call.inbound_msisdn],
    ['Rang from', call.outbound_msisdn],
    ...(showParties ? [['Seller', call.seller_phone] as [string, string | null | undefined]] : []),
  ];
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-2 text-xs leading-5">
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <dt className="text-gray-400">{label}</dt>
          <dd className="truncate font-mono text-gray-700">{value || '—'}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
};

/* ---------- table ---------- */

export const CallsTable: React.FC<{
  calls: VoiceCall[];
  loading: boolean;
  perms: VoicePerms;
  emptyText: string;
  onOpen: (id: string) => void;
}> = ({ calls, loading, perms, emptyText, onOpen }) => {
  const [cancelling, setCancelling] = useState<VoiceCall | null>(null);
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-gray-100 bg-gray-50 text-xs text-gray-500">
            <tr>
              <Th>Time</Th>
              <Th>Buyer</Th>
              <Th>Seller</Th>
              <Th>Listing</Th>
              <Th>Numbers</Th>
              <Th>Box</Th>
              <Th>State</Th>
              <Th right>Talk time</Th>
              <Th>Rec.</Th>
              <Th />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading || calls.length === 0 ? (
              <EmptyRow cols={10} loading={loading}>{emptyText}</EmptyRow>
            ) : (
              calls.map((c) => (
                <tr key={c.session_id} className="cursor-pointer hover:bg-gray-50" onClick={() => onOpen(c.session_id)}>
                  <td className="whitespace-nowrap px-4 py-2 text-gray-700">{fmtShort(c.created_at)}</td>
                  <td className="max-w-[10rem] px-4 py-2"><PartyCell party={c.buyer} fallback={c.buyer_ref} /></td>
                  <td className="max-w-[10rem] px-4 py-2">
                    <PartyCell party={c.seller} fallback={c.seller_ref} />
                    {c.store && <p className="truncate text-xs text-gray-500">{c.store.name || `Store ${c.store.storeid}`}</p>}
                  </td>
                  <td className="max-w-[14rem] truncate px-4 py-2 text-gray-700">{c.listing?.title ?? c.external_reference ?? '—'}</td>
                  <td className="min-w-[13rem] px-4 py-2"><NumbersList call={c} showParties={perms.canRecordings} /></td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-600">{c.box_id ?? '—'}</td>
                  <td className="px-4 py-2"><CallStatePill state={c.state} /></td>
                  <td className="whitespace-nowrap px-4 py-2 text-right text-gray-700">{fmtTalk(c.talk_seconds)}</td>
                  <td className="px-4 py-2">{c.recording_id ? <Mic className="h-4 w-4 text-gray-500" aria-label="Has a recording" /> : null}</td>
                  <td className="px-4 py-2 text-right">
                    <Kebab
                      name={c.session_id}
                      items={[
                        { label: 'View details', icon: <Eye className="h-4 w-4" />, onSelect: () => onOpen(c.session_id) },
                        { label: 'Listen to recording', icon: <Headphones className="h-4 w-4" />, hidden: !(perms.canRecordings && c.recording_id), onSelect: () => onOpen(c.session_id) },
                        { label: 'Copy session id', icon: <Copy className="h-4 w-4" />, onSelect: () => { navigator.clipboard?.writeText(c.session_id); toast.success('Session id copied'); } },
                        { label: 'Cancel call', icon: <PhoneOff className="h-4 w-4" />, danger: true, hidden: !(perms.canManage && isLiveState(c.state)), onSelect: () => setCancelling(c) },
                      ]}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {cancelling && <CancelDialog call={cancelling} onClose={() => setCancelling(null)} />}
    </>
  );
};

/* ---------- filter search ---------- */

const KIND_LABEL: Record<VoiceSuggestion['kind'], string> = {
  phone: 'Number', listing: 'Listing', user: 'Person', org: 'Organization', store: 'Store',
};

/** One box: type a name, email, phone, store or listing id and pick a suggestion.
 *  The pick becomes a removable chip; Enter on a raw ref (user:101) still works. */
const FilterSearch: React.FC<{
  picked: VoiceSuggestion | null;
  onPick: (s: VoiceSuggestion | null) => void;
}> = ({ picked, onPick }) => {
  const [typed, setTyped] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const debounced = useDebounce(typed.trim(), 200);
  const sq = useVoiceSuggestQuery(debounced);
  const hits = debounced.length >= 2 ? sq.data?.suggestions ?? [] : [];

  const choose = (hit: VoiceSuggestion) => { onPick(hit); setTyped(''); setOpen(false); };
  const rawRef = /^(user|org|entity):\d+$/.test(typed.trim());

  if (picked) {
    return (
      <div className="mt-1 flex h-9 items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 text-sm text-blue-900">
        <span className="text-xs text-blue-500">{KIND_LABEL[picked.kind]}</span>
        <span className="max-w-[16rem] truncate font-medium">{picked.label}</span>
        <button type="button" aria-label="Clear search" className="text-blue-500 hover:text-blue-800" onClick={() => onPick(null)}>
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }
  return (
    <div className="relative mt-1">
      <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
      <input
        className={`${INPUT_CLASS} block w-80 pl-8`}
        placeholder="Name, email, phone, store or listing id"
        value={typed}
        onChange={(e) => { setTyped(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, hits.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
          else if (e.key === 'Escape') setOpen(false);
          else if (e.key === 'Enter') {
            e.preventDefault();
            if (hits[active]) choose(hits[active]);
            else if (rawRef) choose({ kind: typed.startsWith('entity') ? 'listing' : typed.startsWith('org') ? 'org' : 'user', label: typed.trim(), ref: typed.trim() });
          }
        }}
      />
      {open && debounced.length >= 2 && (
        <div className="absolute z-20 mt-1 max-h-72 w-96 overflow-auto rounded-md border bg-white py-1 shadow-lg">
          {sq.isFetching && !hits.length ? (
            <p className="px-3 py-2 text-sm text-gray-500">Searching…</p>
          ) : !hits.length ? (
            <p className="px-3 py-2 text-sm text-gray-500">No matches.</p>
          ) : (
            hits.map((hit, n) => (
              <button
                key={`${hit.kind}-${hit.ref ?? hit.phone}-${n}`}
                type="button"
                className={`flex w-full items-baseline gap-2 px-3 py-1.5 text-left text-sm ${n === active ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(n)}
                onClick={() => choose(hit)}
              >
                <span className="w-20 shrink-0 text-xs text-gray-400">{KIND_LABEL[hit.kind]}</span>
                <span className="min-w-0">
                  <span className="block truncate text-gray-900">{hit.label}</span>
                  {hit.sub && <span className="block truncate text-xs text-gray-500">{hit.sub}</span>}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const RANGES: { label: string; days: number | null }[] = [
  { label: 'Today', days: 0 }, { label: '7 days', days: 6 }, { label: '30 days', days: 29 }, { label: 'Any time', days: null },
];

/* ---------- page ---------- */

const STATES = ['', 'live', 'completed', 'failed', 'expired', 'cancelled'];

const VoiceCallsPage: React.FC = () => {
  useBreadcrumbs([{ label: 'Voice calling', href: VOICE_BASE }, { label: 'Calls' }]);
  const perms = useVoicePerms();
  const refresh = useVoiceRefresh();
  const boxes = useVoiceBoxesQuery();
  const [state, setState] = useState('');
  const [box, setBox] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [picked, setPicked] = useState<VoiceSuggestion | null>(null);
  const [kind, setKind] = useState<'' | 'real' | 'test'>('');
  const [recorded, setRecorded] = useState(false);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<string | null>(null);

  const filter: CallsFilter = useMemo(
    () => ({
      state,
      box_id: box,
      ref: picked?.ref,
      phone: picked?.phone,
      kind: kind || undefined,
      recorded: recorded ? '1' : undefined,
      from: from ? moment(from).startOf('day').toISOString() : undefined,
      to: to ? moment(to).endOf('day').toISOString() : undefined,
      page,
    }),
    [state, box, picked, kind, recorded, from, to, page],
  );
  const q = useVoiceCallsQuery(filter, perms.canView);
  const d = q.data;
  const pages = d ? Math.max(1, Math.ceil(d.total / d.per_page)) : 1;
  const reset = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPage(1); };

  if (perms.loading || !perms.canView) {
    return (
      <PageShell title="Calls" description="">
        {perms.loading ? <Skeleton className="h-64 w-full" /> : <AccessDenied />}
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Calls"
      description="Every private call. Live calls refresh every 15 seconds."
      actions={
        <div className="flex items-center gap-2">
          <FetchingChip />
          <Button variant="outline" size="sm" onClick={() => refresh()}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" /> Refresh
          </Button>
        </div>
      }
    >
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-gray-500">
          Search
          <FilterSearch picked={picked} onPick={reset(setPicked)} />
        </label>
        <label className="text-xs text-gray-500">
          State
          <select className={`${INPUT_CLASS} mt-1 block`} value={state} onChange={(e) => reset(setState)(e.target.value)}>
            {STATES.map((s) => <option key={s} value={s}>{s ? titleCase(s) : 'All states'}</option>)}
          </select>
        </label>
        <label className="text-xs text-gray-500">
          Box
          <select className={`${INPUT_CLASS} mt-1 block`} value={box} onChange={(e) => reset(setBox)(e.target.value)}>
            <option value="">All boxes</option>
            {(boxes.data?.boxes ?? []).map((b) => <option key={b.box_id} value={b.box_id}>{b.name || b.box_id}</option>)}
          </select>
        </label>
        <label className="text-xs text-gray-500">
          From
          <input type="date" className={`${INPUT_CLASS} mt-1 block`} value={from} onChange={(e) => reset(setFrom)(e.target.value)} />
        </label>
        <label className="text-xs text-gray-500">
          To
          <input type="date" className={`${INPUT_CLASS} mt-1 block`} value={to} onChange={(e) => reset(setTo)(e.target.value)} />
        </label>
        <label className="text-xs text-gray-500">
          Calls
          <select className={`${INPUT_CLASS} mt-1 block`} value={kind} onChange={(e) => reset(setKind)(e.target.value as '' | 'real' | 'test')}>
            <option value="">Real and test</option>
            <option value="real">Real calls</option>
            <option value="test">Test calls</option>
          </select>
        </label>
        <label className="flex h-9 items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={recorded} onChange={(e) => reset(setRecorded)(e.target.checked)} />
          Recorded only
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {RANGES.map((r) => {
          const start = r.days === null ? '' : moment().subtract(r.days, 'days').format('YYYY-MM-DD');
          const end = r.days === null ? '' : moment().format('YYYY-MM-DD');
          const on = from === start && to === end;
          return (
            <button
              key={r.label}
              type="button"
              className={`rounded-full border px-3 py-1 ${on ? 'border-blue-300 bg-blue-50 text-blue-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
              onClick={() => { setFrom(start); setTo(end); setPage(1); }}
            >
              {r.label}
            </button>
          );
        })}
        {(picked || state || box || kind || recorded || from || to) && (
          <button
            type="button"
            className="ml-1 text-gray-500 underline-offset-2 hover:underline"
            onClick={() => { setPicked(null); setState(''); setBox(''); setKind(''); setRecorded(false); setFrom(''); setTo(''); setPage(1); }}
          >
            Clear all filters
          </button>
        )}
      </div>

      {q.isError && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      {q.isLoading && <LoadingNote>Loading calls.</LoadingNote>}
      <Card title="Calls" note={d ? `${d.total.toLocaleString()} found` : undefined} flush>
        <CallsTable
          calls={d?.calls ?? []}
          loading={q.isLoading}
          perms={perms}
          emptyText="No calls match these filters."
          onOpen={setOpen}
        />
        <div className="flex items-center justify-between border-t border-gray-100 px-4 py-2 text-xs text-gray-500">
          <span>Page {page} of {pages}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft className="h-3.5 w-3.5" /> Previous
            </Button>
            <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
              Next <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </Card>
      <CallSheet sessionId={open} perms={perms} onClose={() => setOpen(null)} />
    </PageShell>
  );
};

export default VoiceCallsPage;
